<template>
  <v-select
    v-if="programs.length > 1"
    id="program-switcher"
    v-model="selectedProgram"
    class="py-2 mr-4"
    density="compact"
    :items="programs"
    :item-props="itemProps"
    width="300"
    :menu-props="{ maxHeight: 600, origin: 'overlap', width: 450 }"
    variant="outlined"
    hide-details
  />
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useAppContextStore } from '@/stores/appContext'
import { useAuthenticationStore } from '@/stores/authentication'
import { useNotificationStore } from '@/stores/notification'
import { useProgramDataStore } from '@/stores/programData'
import { useSettingsStore } from '@/stores/settings'

import { FORM_NAVIGATION_WARNING } from '@/constants'
import routeNames from '@/router/routeNames'
import { hasMatchingId } from '@/utils/compare'

import type { UserProgram } from '@policyfly/protobuf/patrick'

export default defineComponent({
  name: 'ProgramSwitcher',

  setup () {
    const notificationStore = useNotificationStore()

    const authenticationStore = useAuthenticationStore()
    const programs = computed<UserProgram[]>(() => {
      return [...authenticationStore.programs]
        .sort((a, b) => {
          if (a.client === b.client) {
            return a.name.localeCompare(b.name)
          }
          return a.client.localeCompare(b.client)
        })
    })
    const selectedProgram = computed({
      get (): UserProgram {
        return authenticationStore.currentProgram!
      },
      set (program: UserProgram) {
        selectProgram(program)
      },
    })

    const route = useRoute()
    const router = useRouter()
    const programDataStore = useProgramDataStore()
    const appContextStore = useAppContextStore()
    const settingsStore = useSettingsStore()
    async function selectProgram (program: UserProgram): Promise<void> {
      if (hasMatchingId(program, authenticationStore.programIds)) return
      if (route.meta?.form && !await notificationStore.confirm({ title: FORM_NAVIGATION_WARNING })) return
      programDataStore.resetProgramData()
      authenticationStore.programIds = {
        id: program.id,
        bingoId: program.bingoId,
      }
      appContextStore.clearApplicationData()
      await settingsStore.load()
      await router.replace({ name: routeNames.HOME })
    }

    function itemProps (program: UserProgram): { title: string } {
      return {
        title: `${program.client} - ${program.name}`,
      }
    }

    return {
      programs,
      selectedProgram,
      itemProps,
    }
  },
})
</script>

<style lang="sass" scoped>
.program-switcher-activator
  border-bottom: 1px solid white
  span
    line-height: 24px
  .icon
    margin-left: 20px
.tile-title
  padding: 0px 79px 0px 0px
</style>
